import { SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Modal, message, Switch } from 'antd';
import debounce from 'lodash/debounce';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import type React from 'react';
import { useState } from 'react';
import { useGetPlatformsData, useUpdateBrandsAction } from 'src/hooks/data/platforms';
import type { GetPlatformsResponse, UpdateBrandsPayload } from 'src/types/platforms';
import { PlatformsTable } from './platforms-table/platforms-table';

const prepareChangedBrandsPayload = (
  platforms: GetPlatformsResponse | undefined,
  editedBrands: UpdateBrandsPayload,
): UpdateBrandsPayload => {
  const originalBrandsById = keyBy(
    platforms?.flatMap((platform) => platform.brands),
    'id',
  );

  return editedBrands.reduce<UpdateBrandsPayload>((prev, cur) => {
    const originalBrand = originalBrandsById[cur.id];

    if (!originalBrand) {
      return prev;
    }

    const changedBrand: UpdateBrandsPayload[0] = { id: cur.id };

    if (cur.cmsStatus && cur.cmsStatus !== originalBrand.cmsStatus) {
      changedBrand.cmsStatus = cur.cmsStatus;
    }

    if (cur.priority && cur.priority !== originalBrand.priority) {
      changedBrand.priority = cur.priority;
    }

    if (changedBrand.cmsStatus || changedBrand.priority) {
      prev.push(changedBrand);
    }

    return prev;
  }, []);
};

interface AvailableBrandsModalProps {
  isOpen: boolean;
  close: () => void;
}

export const AvailableBrandsModal: React.FC<AvailableBrandsModalProps> = ({ isOpen, close }) => {
  const { data } = useGetPlatformsData({ withInactive: true });
  const { updateBrands, isInProgress } = useUpdateBrandsAction();

  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [editedBrands, setEditedBrands] = useState<UpdateBrandsPayload>([]);

  const closeWithReset = () => {
    close();
    setSearchQuery('');
    setShowOnlyNew(false);
    setEditedBrands([]);
  };

  const handleClose = () => {
    const payload = prepareChangedBrandsPayload(data, editedBrands);

    if (payload.length > 0) {
      Modal.confirm({
        content: 'You have unsaved changes. Are you sure you want to close?',
        centered: true,
        onOk: closeWithReset,
        okText: 'Close',
        cancelText: 'Cancel',
      });
      return;
    }

    closeWithReset();
  };

  const onInputChange = debounce<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    setSearchQuery(e.target.value);
  }, 300);

  const onBrandsChange = (payload: UpdateBrandsPayload) => {
    setEditedBrands((prev) => {
      const newBrands = keyBy(prev, 'id');

      payload.forEach((brand) => {
        if (newBrands[brand.id]) {
          merge(newBrands[brand.id], brand);
        } else {
          newBrands[brand.id] = brand;
        }
      });

      return Object.values(newBrands);
    });
  };

  const onSave = () => {
    const payload = prepareChangedBrandsPayload(data, editedBrands);

    if (payload.length === 0) {
      message.info('No changes to save');
      return;
    }

    updateBrands(payload, {
      onSuccess: () => {
        message.success('Brands have been updated successfully');
        closeWithReset();
      },
      onError: () => {
        message.error('Brands have not been updated');
      },
    });
  };

  return (
    <Modal
      width={1064}
      title={
        <div className="flex justify-between items-center pr-36">
          <span>Available Brands</span>
          <Button type="primary" loading={isInProgress} onClick={onSave}>
            Save
          </Button>
        </div>
      }
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      classNames={{ body: 'p-0' }}
      centered
      destroyOnHidden
    >
      <div className="pb-6">
        <div style={{ height: 'calc(80vh - 57px)' }} className="overflow-y-scroll">
          <div className="px-24 pb-24 pt-10">
            <Alert description="Hidden brands are not displayed in the CMS" showIcon />

            <div className="flex items-center gap-x-12 my-10">
              <Input
                prefix={
                  <span className="text-gray-7">
                    <SearchOutlined />
                  </span>
                }
                placeholder="Search by Plarform or Brand"
                onChange={onInputChange}
                allowClear
                className="max-w-[260px]"
              />

              <div className="flex items-center">
                <Switch checked={showOnlyNew} onChange={setShowOnlyNew} size="small" />
                <span
                  className="pl-4 text-[12px]/[20px] cursor-pointer"
                  onClick={() => setShowOnlyNew((prev) => !prev)}
                >
                  Show Only New Brands
                </span>
              </div>
            </div>

            <PlatformsTable
              searchQuery={searchQuery}
              showOnlyNew={showOnlyNew}
              editedBrands={editedBrands}
              onBrandsChange={onBrandsChange}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
